import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCreateAdministratorMutation } from '@store/api/setupEndpoints';
import { serverMessage } from '@store/error';
import { gray } from '@theme/primitives';
import React, { useState } from 'react';

/**
 * First run. The only thing an unclaimed server offers, and the only chance to create the account
 * that administers it: no credentials are shipped in the image, so there is nothing to sign in
 * with until this succeeds.
 *
 * On success it does not sign the administrator in. Creating an account and issuing a session are
 * separate things, and keeping them separate means there is one code path that starts a session
 * and one place a failed sign-in is recorded.
 */
const CreateAdministratorCard = () => {
  const [createAdministrator, { isLoading }] = useCreateAdministratorMutation();

  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState<string>();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);

    const formData = new FormData(event.currentTarget);
    const username = formData.get('username')?.toString().trim() ?? '';
    const password = formData.get('password')?.toString() ?? '';
    const confirmation = formData.get('confirmation')?.toString() ?? '';

    if (!validate(username, password, confirmation)) return;

    try {
      await createAdministrator({ username, password }).unwrap();
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The administrator account could not be created.');
    }
  };

  const validate = (username: string, password: string, confirmation: string): boolean => {
    const usernameProblem = username.length < 3 ? 'Choose a username of at least 3 characters.' : '';

    // Mirrors the server's rule so the answer arrives while the person is still typing. The server
    // enforces it regardless; this is courtesy, not a control.
    const passwordProblem =
      password.length < 12 ? 'Use at least 12 characters.'
        : password !== confirmation ? 'The two passwords do not match.'
          : !isStrongEnough(password)
            ? 'Include an uppercase letter, a lowercase letter, and a digit or a symbol.'
            : '';

    setUsernameError(usernameProblem);
    setPasswordError(passwordProblem);

    return !usernameProblem && !passwordProblem;
  };

  return (
    <Card variant='outlined'>
      <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
        Set up this server
      </Typography>

      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        This license server has no administrator yet, so it is serving nothing else. Create the
        account that will administer it. It is the only one, and no password was shipped with the
        image.
      </Typography>

      <Box component='form' onSubmit={handleSubmit} noValidate sx={formBoxStyles}>
        <FormControl>
          <FormLabel htmlFor='setup-username'>Username</FormLabel>
          <TextField
            error={!!usernameError}
            helperText={usernameError}
            id='setup-username'
            name='username'
            type='text'
            placeholder='admin'
            autoComplete='username'
            autoFocus
            required
            fullWidth
            variant='outlined'
            color={usernameError ? 'error' : 'primary'}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor='setup-password'>Password</FormLabel>
          <TextField
            error={!!passwordError}
            helperText={passwordError || 'At least 12 characters, with an uppercase letter, a lowercase letter, and a digit or symbol.'}
            id='setup-password'
            name='password'
            type='password'
            placeholder='••••••••••••'
            autoComplete='new-password'
            required
            fullWidth
            variant='outlined'
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor='setup-confirmation'>Confirm password</FormLabel>
          <TextField
            error={!!passwordError}
            id='setup-confirmation'
            name='confirmation'
            type='password'
            placeholder='••••••••••••'
            autoComplete='new-password'
            required
            fullWidth
            variant='outlined'
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>

        {error && <Alert severity='error'>{error}</Alert>}

        <Button type='submit' fullWidth variant='contained' disabled={isLoading}>
          {isLoading ? 'Creating…' : 'Create administrator'}
        </Button>
      </Box>
    </Card>
  );
};

const isStrongEnough = (password: string): boolean => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(password);

  return hasUpperCase && hasLowerCase && (hasNumber || hasSpecialChar);
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  '&.MuiPaper-root': { background: '#05070A66 !important' },
  '& .MuiOutlinedInput-root': { background: `${gray[900]} !important` },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const formBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
};

export default CreateAdministratorCard;
